import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  AccreditationBreakdown,
  CategoryBreakdownItem,
  CertificateRollupItem,
  CertificatesSummaryData,
} from '../types'

dayjs.extend(utc)

/**
 * Activity types treated as "courses & events" for the accredited /
 * not-accredited hours split — excludes external certificates and
 * attendance-only records, which aren't accreditation-bearing activities.
 */
let ACCREDITATION_SCOPED_TYPES: CertificateRollupItem['type'][] = [
  'recorded',
  'live',
  'event',
]

/**
 * Activity types that appear in the Activity Types breakdown, in display order.
 * Mirrors the server-side ordering used by the certificates summary endpoint.
 */
export let ACTIVITY_TYPE_ORDER: CertificateRollupItem['type'][] = [
  'recorded',
  'live',
  'external',
  'event',
  'attendance',
]

/**
 * Filter rollup items to those whose completion date falls within the
 * inclusive [startDate, endDate] range. A null bound leaves that side open.
 */
export function filterRollupByDateRange(
  data: CertificateRollupItem[],
  startDate: string | null,
  endDate: string | null,
): CertificateRollupItem[] {
  if (!startDate && !endDate) return data

  // Compare on the UTC calendar day. Completion dates come back from the API as
  // UTC instants (external-certificate dates are stored as UTC midnight), so
  // interpreting them — and the picked bounds — in local time would shift the
  // day and drop items that actually fall on the boundary. See the query-param
  // note in use-certificates-date-filter.ts.
  let start = startDate ? dayjs.utc(startDate).startOf('day') : null
  let end = endDate ? dayjs.utc(endDate).endOf('day') : null

  return data.filter((item) => {
    let completed = dayjs.utc(item.completionDate)
    // An item with an unparseable completion date can't logically fall within
    // a requested range, so exclude it rather than letting the always-false
    // Dayjs comparisons silently keep it.
    if (!completed.isValid()) return false
    if (start && completed.isBefore(start)) return false
    if (end && completed.isAfter(end)) return false
    return true
  })
}

/**
 * Group rollup items by category, accumulating total hours, completion
 * count, and most recent completion date per category.
 *
 * Accumulates into a Map rather than spreading a plain object per item: a
 * fresh `{ ...acc, [key]: ... }` on every rollup item is O(n) copying that
 * scales badly on large datasets and works against the goal of deferring
 * heavy recomputation. Each iteration only ever constructs one small,
 * constant-size entry object, and `Map.set` keeps us clear of the
 * fp/no-mutation property-assignment rule.
 */
function buildCategoryBreakdown(
  data: CertificateRollupItem[],
): CategoryBreakdownItem[] {
  let categoryMap = data.reduce<Map<string, CategoryBreakdownItem>>(
    (acc, item) => {
      let key = item.category || 'Uncategorized'
      let existing = acc.get(key)
      return acc.set(
        key,
        existing
          ? {
              categoryName: key,
              totalHours: existing.totalHours + item.ceHours,
              completionCount: existing.completionCount + 1,
              mostRecentCompletion: dayjs
                .utc(item.completionDate)
                .isAfter(dayjs.utc(existing.mostRecentCompletion))
                ? item.completionDate
                : existing.mostRecentCompletion,
            }
          : {
              categoryName: key,
              totalHours: item.ceHours,
              completionCount: 1,
              mostRecentCompletion: item.completionDate,
            },
      )
    },
    new Map(),
  )

  return Array.from(categoryMap.values())
}

/**
 * Recompute the certificates summary client-side from a set of rollup items.
 * Mirrors the aggregation performed server-side by the certificates summary
 * endpoint, so the Overview cards and Activity Types breakdown can reflect a
 * user-selected date range without any backend changes.
 */
export function computeSummaryFromRollup(
  data: CertificateRollupItem[],
): CertificatesSummaryData {
  let totalActivities = data.length
  let totalCEHours = data.reduce((sum, item) => sum + item.ceHours, 0)
  let totalCertificates = data.filter((item) => item.certificateID).length

  let validDates = data
    .map((item) => dayjs.utc(item.completionDate))
    .filter((date) => date.isValid())

  let firstDate = validDates.reduce<ReturnType<typeof dayjs> | null>(
    (earliest, date) =>
      !earliest || date.isBefore(earliest) ? date : earliest,
    null,
  )
  let lastDate = validDates.reduce<ReturnType<typeof dayjs> | null>(
    (latest, date) => (!latest || date.isAfter(latest) ? date : latest),
    null,
  )

  // Months spanned (inclusive), min 1, so a single-month range doesn't divide by zero.
  let monthsSpanned =
    firstDate && lastDate
      ? Math.max(1, lastDate.diff(firstDate, 'month') + 1)
      : 1
  let averageHoursPerMonth =
    totalActivities > 0 ? totalCEHours / monthsSpanned : 0

  let activityBreakdown = ACTIVITY_TYPE_ORDER.map((activityType) => {
    let items = data.filter((item) => item.type === activityType)
    let count = items.length
    let hours = items.reduce((sum, item) => sum + item.ceHours, 0)
    return {
      activityType,
      count,
      totalHours: hours,
      percentage: totalActivities > 0 ? (count / totalActivities) * 100 : 0,
    }
  }).filter((entry) => entry.count > 0)

  let assignedCompletions = data.filter((item) => item.assignment).length
  let selfPacedCompletions = totalActivities - assignedCompletions
  let assignmentComplianceRate =
    totalActivities > 0 ? (assignedCompletions / totalActivities) * 100 : 0

  return {
    totalStats: {
      totalCEHours,
      totalActivities,
      totalCertificates,
      firstActivityDate: firstDate ? firstDate.toISOString() : '',
      mostRecentActivityDate: lastDate ? lastDate.toISOString() : '',
      averageHoursPerMonth,
    },
    activityBreakdown,
    categoryBreakdown: buildCategoryBreakdown(data),
    courseFrequency: [],
    assignmentStats: {
      assignedCompletions,
      selfPacedCompletions,
      assignmentComplianceRate,
    },
  }
}

/**
 * Category breakdown scoped to a single activity type — drives the
 * category drill-down shown when a segment in the Activity Distribution
 * chart is selected. Hours only, no percentage (per design).
 */
export function computeCategoryBreakdownForType(
  data: CertificateRollupItem[],
  type: CertificateRollupItem['type'],
): CategoryBreakdownItem[] {
  return buildCategoryBreakdown(data.filter((item) => item.type === type))
}

/**
 * Accredited vs. not-accredited CE hours, scoped to courses & events
 * (recorded, live, and event activity types) per the design.
 */
export function computeAccreditationBreakdown(
  data: CertificateRollupItem[],
): AccreditationBreakdown {
  let scoped = data.filter((item) =>
    ACCREDITATION_SCOPED_TYPES.includes(item.type),
  )
  let accreditedHours = scoped
    .filter((item) => item.accreditationStatus === 'Accredited')
    .reduce((sum, item) => sum + item.ceHours, 0)
  let notAccreditedHours = scoped
    .filter((item) => item.accreditationStatus !== 'Accredited')
    .reduce((sum, item) => sum + item.ceHours, 0)

  return { accreditedHours, notAccreditedHours }
}
